<template>
  <template v-if="allowed || aProposition">
    <!-- Pastille d'appel, présente sur tous les écrans du périmètre interne.
         L'infobulle dit à quoi sert Luce : une pastille seule n'apprend rien à
         qui la découvre, et personne ne clique sur ce qu'il ne comprend pas. -->
    <el-tooltip
      placement="left"
      :show-after="200"
      effect="dark"
    >
      <template #content>
        <span class="luce-tip">
          <strong>Luce, l'assistant interne</strong>
          Posez une question, faites rédiger ou reformuler un texte,<br>
          ou demandez où se trouve un écran de gestion-v2.
        </span>
      </template>

      <button
        v-show="!open"
        type="button"
        class="luce-launcher"
        aria-label="Ouvrir Luce, l'assistant interne"
        @click="open = true"
      >
        <LuceMark :size="52" />
      </button>
    </el-tooltip>

    <!-- Panneau latéral, et non `el-drawer` : le drawer d'Element Plus monte un
         `ElFocusTrap` actif dès qu'il est ouvert, `modal` ou non. Le focus serait
         ramené dans le panneau à chaque clic ailleurs — ce qui interdit
         précisément ce qu'on cherche ici : continuer à travailler pendant qu'on
         lui parle. Un aside maison n'a ni piège à focus, ni verrou de
         défilement, et son CSS reste scopé faute d'être téléporté. -->
    <Transition name="luce-panel">
      <aside
        v-show="open"
        class="luce-panel"
        role="dialog"
        aria-modal="false"
        aria-label="Luce, l'assistant interne"
      >
        <header class="luce-panel-head">
          <LuceWordmark class="luce-panel-signature" />

          <!-- Commandes de panneau, pas actions métier : icônes seules,
               groupées à droite, qui ne se colorent qu'au survol. -->
          <el-tooltip
            v-if="allowed"
            content="Ouvrir en plein écran"
            placement="bottom"
            :show-after="400"
            :trigger="['hover', 'focus']"
          >
            <button
              type="button"
              class="luce-panel-btn"
              aria-label="Ouvrir Luce en plein écran"
              @click="openFullPage"
            >
              <MdiIcon
                name="ArrowExpand"
                :size="16"
              />
            </button>
          </el-tooltip>

          <el-tooltip
            content="Fermer"
            placement="bottom"
            :show-after="400"
            :trigger="['hover', 'focus']"
          >
            <button
              type="button"
              class="luce-panel-btn"
              aria-label="Fermer Luce"
              @click="open = false"
            >
              <MdiIcon
                name="Close"
                :size="18"
              />
            </button>
          </el-tooltip>
        </header>

        <!-- Deux onglets seulement quand les deux existent : un agent sans accès
             à la conversation ne voit que la proposition, et inversement. -->
        <nav
          v-if="allowed && aProposition"
          class="luce-panel-tabs"
          aria-label="Contenu du panneau"
        >
          <button
            type="button"
            class="luce-panel-tab"
            :class="{ 'is-active': vue === 'proposition' }"
            @click="vue = 'proposition'"
          >
            Proposition
          </button>
          <button
            type="button"
            class="luce-panel-tab"
            :class="{ 'is-active': vue === 'chat' }"
            @click="vue = 'chat'"
          >
            Conversation
          </button>
        </nav>

        <LucePropositionPanel
          v-if="aProposition"
          v-show="vue === 'proposition' || !allowed"
        />

        <!-- `mounted` et non `v-if="open"` : le panneau garde la conversation
             entre deux ouvertures, mais rien n'est chargé tant que Luce n'a pas
             été ouverte au moins une fois. -->
        <LuceChat
          v-if="mounted && allowed"
          v-show="vue === 'chat' || !aProposition"
          class="luce-panel-chat"
          compact
          docked
        />
      </aside>
    </Transition>
  </template>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { usePermissions } from '@/composables/usePermissions';
import { P } from '@/config/permissions';
import LuceMark from '@/components/Luce/LuceMark.vue';
import LuceWordmark from '@/components/Luce/LuceWordmark.vue';
import LuceChat from '@/views/ai/components/LuceChat.vue';
import LucePropositionPanel from '@/components/Luce/LucePropositionPanel.vue';
import { useLuceProposition } from '@/composables/useLuceProposition';

/**
 * Point d'entrée flottant vers Luce, monté dans le layout interne.
 *
 * Panneau latéral et non fenêtre modale : on doit pouvoir continuer à naviguer
 * dans gestion-v2 pendant la conversation — demander « où change-t-on ça ? »,
 * puis y aller sans fermer Luce ni perdre le fil. Le composant vit dans le
 * layout, il survit donc aux changements de route.
 *
 * Le bouton n'apparaît que pour qui détient la permission ; le backend refait
 * le contrôle sur chaque appel, ce masquage n'est qu'un confort d'affichage.
 */

const router = useRouter();
const { can } = usePermissions();

// En `computed` et non en valeur figée : le layout se monte avant que les
// permissions ne soient toutes chargées, un booléen calculé une fois resterait
// à faux et la pastille n'apparaîtrait jamais.
const allowed = computed(() => can([P.AI.CHAT]));

const open = ref(false);
// Le chat n'est instancié qu'à la première ouverture, puis conservé : rouvrir
// le panneau retrouve la conversation en cours au lieu de repartir de zéro.
const mounted = ref(false);

watch(open, (value) => {
  if (value) mounted.value = true;
});

// La proposition de réponse d'un ticket relève du support, pas de la conversation : elle
// s'affiche pour qui peut répondre au ticket, même sans accès au reste de Luce.
const { proposition } = useLuceProposition();
const aProposition = computed(() => proposition.ticketId !== null && can([P.TICKETS.MANAGE]));
const vue = ref('chat');

// Une proposition prête ouvre le panneau sur elle. Fermé par l'agent, il ne se rouvre qu'à la
// proposition suivante — pas à chaque changement de l'état.
watch(() => proposition.ouvertures, () => {
  vue.value = 'proposition';
  open.value = true;
});

watch(aProposition, (present) => {
  if (!present) vue.value = 'chat';
});

function openFullPage() {
  open.value = false;
  router.push({ name: 'LuceChat' });
}

// Un panneau non modal ne reçoit pas les touches d'Element Plus : Échap est
// recâblé ici. On n'intercepte que si Luce est ouverte, pour ne pas voler la
// touche aux fenêtres de l'application par-dessus lesquelles elle peut vivre.
function onKeydown(event) {
  if (event.key === 'Escape' && open.value) open.value = false;
}

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<style scoped>
.luce-launcher {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2000;
  display: flex;
  padding: 0;
  border: 0;
  /* La marque porte sa propre tuile : le bouton n'est plus qu'un cadre. */
  border-radius: 14px;
  cursor: pointer;
  background: none;
  box-shadow: 0 6px 18px rgb(0 0 0 / 22%);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.luce-launcher:hover {
  transform: translateY(-2px);
  box-shadow: 0 9px 22px rgb(0 0 0 / 28%);
}

.luce-launcher:focus-visible {
  outline: 2px solid var(--el-color-primary);
  outline-offset: 3px;
}

.luce-tip {
  display: block;
  max-width: 270px;
  line-height: 1.5;
}

.luce-tip strong {
  display: block;
  margin-bottom: 3px;
}

/* Le panneau. Il flotte au-dessus du contenu plutôt que de le pousser : pousser
   demanderait au layout de se réorganiser à chaque ouverture, et ferait sauter
   la page sous les yeux de qui est en train d'y lire quelque chose. */
.luce-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2001;
  display: flex;
  flex-direction: column;
  width: 440px;
  max-width: 100vw;
  background: var(--el-bg-color, #fff);
  box-shadow: -10px 0 30px -12px rgb(2 38 74 / 28%);
}

.luce-panel-tabs {
  display: flex;
  flex: none;
  border-bottom: 1px solid var(--el-border-color-lighter);
}
.luce-panel-tab {
  flex: 1;
  padding: 10px 12px;
  border: 0;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  background: none;
}
.luce-panel-tab:hover {
  color: var(--el-text-color-primary);
}
.luce-panel-tab.is-active {
  border-bottom-color: var(--el-color-primary);
  font-weight: 600;
  color: var(--el-color-primary);
}
.luce-panel-tab:focus-visible {
  outline: 2px solid var(--el-color-primary);
  outline-offset: -2px;
}
.luce-panel-head {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: none;
  padding: 12px 12px 12px 16px;
  background: #02264a;
}

.luce-panel-signature {
  height: 30px;
  /* L'encre de la signature suit `currentColor`, que le bandeau met au clair. */
  color: #e8f1fa;
  margin-right: auto;
}

.luce-panel-btn {
  display: flex;
  align-items: center;
  padding: 5px;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  /* Discret au repos, franc au survol : présent sans réclamer l'attention. */
  color: rgb(232 241 250 / 55%);
  background: none;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.luce-panel-btn:hover {
  color: #fff;
  background: rgb(255 255 255 / 12%);
}

.luce-panel-btn:focus-visible {
  color: #fff;
  outline: 2px solid rgb(255 255 255 / 70%);
  outline-offset: 1px;
}

.luce-panel-chat {
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px;
}

.luce-panel-enter-active,
.luce-panel-leave-active {
  transition: transform 0.22s ease;
}

.luce-panel-enter-from,
.luce-panel-leave-to {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .luce-panel-enter-active,
  .luce-panel-leave-active {
    transition: none;
  }
}

@media (max-width: 640px) {
  .luce-panel {
    width: 100vw;
  }
}
</style>
